import api from '../services/api';
import alertService from './alert';

async function fetchInvoicePdfBlob(invoiceId, mode) {
  const response = await api.get(`/crm/invoices/${invoiceId}/pdf`, {
    responseType: 'blob',
    params: mode ? { mode } : undefined,
  });
  return new Blob([response.data], { type: 'application/pdf' });
}

function extractErrorMessage(err, fallback) {
  // responseType: 'blob' means an error JSON body also arrives as a Blob,
  // not parsed JSON — read it as text and parse manually, otherwise
  // err.response.data.message is always undefined.
  if (err.response?.data instanceof Blob) {
    return err.response.data.text().then(text => {
      try {
        return JSON.parse(text)?.message || fallback;
      } catch {
        return fallback;
      }
    });
  }
  return Promise.resolve(err.response?.data?.message || fallback);
}

export async function downloadInvoicePdf(invoice) {
  try {
    const blob = await fetchInvoicePdfBlob(invoice._id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${invoice.invoiceNumber}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    const message = await extractErrorMessage(err, 'Could not download invoice PDF');
    alertService.error('Download Failed', message);
  }
}

export async function previewInvoicePdf(invoice) {
  // window.open must run synchronously inside the click handler — once we
  // `await` the PDF fetch first, browsers no longer treat a later
  // window.open as user-gesture-triggered and silently block it. Opening a
  // blank tab up front and filling in its location once the blob is ready
  // avoids that.
  const tab = window.open('', '_blank');
  try {
    const blob = await fetchInvoicePdfBlob(invoice._id, 'inline');
    const url = URL.createObjectURL(blob);
    if (tab) {
      tab.location.href = url;
    } else {
      // Popup was blocked outright (e.g. browser setting) — fall back to
      // navigating the current tab away isn't acceptable, so surface it.
      alertService.error('Preview Blocked', 'Your browser blocked the preview tab. Please allow pop-ups for this site and try again.');
    }
    // Give the new tab a moment to load the blob before revoking it.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    tab?.close();
    const message = await extractErrorMessage(err, 'Could not preview invoice PDF');
    alertService.error('Preview Failed', message);
  }
}
